/**
 * Status badges displayed in the single-order header.
 *
 * Initiation vs continuation chip: from all J-codes’ `initiation_continuation_status`
 * on Postgres `auth_on_file` (`entries` / `auth_entries` only; no flowsheet).
 * NYCBS “pharm fill in history” chip: from `order_tags` on the same payload when present.
 * Denial chips still come from `useHeaderBadges` / J-code `drug_preference` & `nccn_match`.
 * The CPT Description column
 * only shows triangle / red-flag icons for line-level risks, not a green tick.
 * @module headerStatusBadges
 */
import BadgeWithIcon from "../../../../../components/badgeWithIcon/badgeWithIcon";
import BadgeWithIconRounded from "../../../../../components/badgeWithIconRounded/badgeWithIconRounded";
import TagWithIcon from "../../../../../components/tagWithIcon/tagWithIcon";
import { BadgeWithIconProps } from "../../../../../data-model/badgeWithIconProps";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { getBadgeDetailsAndIconColorForCareTag } from "../../busniessOffice/utils/getBadgeAndIconColorForCareTag";
import { getDenialRiskRoundedBadgeConfig } from "../utils/denialRiskHeaderBadges";
import {
  getNycbsPharmFillInHistoryBadgeAppearance,
  hasNycbsPharmFillInHistoryOrderTag,
} from "../utils/nycbsPharmFillInHistoryHeaderBadge";
import { resolveRegimenCareTagForHeader } from "../utils/regimenCareHeaderChip";

/**
 * Props for {@link HeaderStatusBadges}.
 */
interface HeaderStatusBadgesProps {
  getStatusBadge?: BadgeWithIconProps;
  generalOrderDetails?: GeneralOrderDetails;
  authOnFileDetails?: AuthOnFile;
  allDenialRiskLabels?: string[];
}

/**
 * Renders auth status, optional regimen tag, optional NYCBS `order_tags` chip,
 * denial-risk chips when present, and alert pills.
 *
 * @param props - Badge data from the same sources as useHeaderBadges / getAllOrderDenialRiskLabels
 * @returns Status badge row
 */
const HeaderStatusBadges = ({
  getStatusBadge,
  generalOrderDetails,
  authOnFileDetails,
  allDenialRiskLabels = [],
}: HeaderStatusBadgesProps) => {
  const hoverText = generalOrderDetails?.metadata?.alerts?.join(", ") || "";
  const regimenTag = resolveRegimenCareTagForHeader(authOnFileDetails);
  const regimenBadge =
    regimenTag !== ""
      ? getBadgeDetailsAndIconColorForCareTag(regimenTag)
      : null;

  const nycbsPharmFillBadge = hasNycbsPharmFillInHistoryOrderTag(
    authOnFileDetails,
  )
    ? getNycbsPharmFillInHistoryBadgeAppearance()
    : null;

  return (
    <div className="authorization-layout--header__left--status ml-4 flex flex-row flex-wrap items-center gap-2">
      <BadgeWithIcon
        text={getStatusBadge?.text ?? ""}
        id={getStatusBadge?.id ?? ""}
        bgColor={getStatusBadge?.bgColor ?? ""}
        textColor={getStatusBadge?.textColor ?? ""}
      />

      {regimenBadge != null && (
        <BadgeWithIconRounded
          text={regimenBadge.badgeText}
          icon={
            <div
              style={{
                width: "10px",
                height: "10px",
                background: regimenBadge.iconColor,
                borderRadius: "9999px",
              }}
            />
          }
          id="header-regimen-care-tag"
          bgColor={regimenBadge.badgeBgColor}
          textColor={regimenBadge.badgeTextColor}
          borderColor={regimenBadge.borderColor}
        />
      )}

      {nycbsPharmFillBadge != null && (
        <BadgeWithIconRounded
          text={nycbsPharmFillBadge.badgeText}
          icon={
            <div
              style={{
                width: "10px",
                height: "10px",
                background: nycbsPharmFillBadge.iconColor,
                borderRadius: "9999px",
              }}
            />
          }
          id="header-nycbs-pharm-fill-in-history-tag"
          bgColor={nycbsPharmFillBadge.badgeBgColor}
          textColor={nycbsPharmFillBadge.badgeTextColor}
          borderColor={nycbsPharmFillBadge.borderColor}
        />
      )}

      <div className="flex flex-wrap gap-1">
        {generalOrderDetails?.metadata?.alert_badges?.map((badge, index) => (
          <TagWithIcon
            key={index}
            text={badge}
            textOnHover={hoverText}
            maxWidth="20rem"
            placement="bottom"
          />
        ))}
      </div>

      {allDenialRiskLabels.length > 0 && (
        <div className="flex flex-row flex-wrap items-center gap-3">
          {allDenialRiskLabels.map((label) => {
            const cfg = getDenialRiskRoundedBadgeConfig(label);
            return (
              <BadgeWithIconRounded
                key={label}
                text={cfg.text}
                id={cfg.id}
                bgColor={cfg.bgColor}
                textColor={cfg.textColor}
                borderColor={cfg.borderColor}
                icon={cfg.icon}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeaderStatusBadges;
